import { ActivePageType, IMeal, IProduct } from "../types/types"
import Button from "@mui/material/Button";
import { MealItem } from "./MealItem";
import { SetStateAction, useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "./Snackbar";
import { useCart } from "../hooks/useCart";
import { Spinner } from "./spinner/Spinner";
import { mergeMealsToProducts } from "../utils/mergeMealsToProducts";
import { useGetNewToken } from "../hooks/useGetNewToken";
import { useMenu } from "../hooks/useMenu";

export const Cart = ({ setList, setActivePage }: { setList: (value: SetStateAction<IProduct[]>) => void, setActivePage: (value: SetStateAction<ActivePageType>) => void }) => {
  const { cart, setCart, loading: cartLoading } = useCart();
  const { setMenu } = useMenu();
  const breakfast = cart.filter(({ type }) => type === "breakfast")
  const lunch = cart.filter(({ type }) => type === "lunch")
  const dinner = cart.filter(({ type }) => type === "dinner")
  const snack = cart.filter(({ type }) => type === "snack")
  const { Snackbar, snackbarStatus, setSnackbarStatus } = useSnackbar();
  const { getNewToken } = useGetNewToken();

  const handleSetCart = useCallback(async (updater: ((prev: IMeal[]) => IMeal[]) | IMeal[]) => {
    let value = cart
    if (typeof updater === "function") {
      value = updater(value);
    } else {
      value = updater;
    }

    setCart(value);
    setMenu(value);
  }, [cart, setCart, setMenu])

  const clearCart = () => {
    setCart([])
    localStorage.removeItem("cart");
  }

  const addToList = () => {
    const { products, localProducts } = mergeMealsToProducts(cart)
    setList([...products, ...localProducts])
    clearCart();
  }

  const handleOrder = async () => {
    setSnackbarStatus("working");
    const { products, localProducts } = mergeMealsToProducts(cart)
    setList(localProducts)

    const addToCart = async () => {
      const userData = localStorage.getItem('userData');
      const parsedUserData = userData ? JSON.parse(userData) : {}
      const res = await fetch("https://frisco-meal-order-api.vercel.app/api/add-to-cart", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products, userData: parsedUserData })
      });

      const data = await res.json();
      return data
    };

    try {

      let data = await addToCart();

      if (!data.success) {
        let tokenData = null;

        while (!tokenData) {
          console.log('Generating new token');
          tokenData = await getNewToken(false);
          if (!tokenData) await new Promise((r) => setTimeout(r, 500));
        }

        data = await addToCart();
      }

      setSnackbarStatus("done");
      setCart([]);
      localStorage.removeItem("cart");
      setActivePage("list")
    } catch (e) {
      console.error(e);
      setSnackbarStatus("error");
    } finally {
      setTimeout(() => {
        setSnackbarStatus("idle")
      }, 2000)
    }
  };

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const mealWithQuantityNull = cart.find((meal) => meal.quantity === 0);
    if (mealWithQuantityNull) {
      handleSetCart((meals) => meals.filter((meal) => meal.quantity !== 0));
    }
  }, [cart, handleSetCart])

  if (cartLoading) {
    return <Spinner />
  }

  if (!cart.length) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: "center", flexDirection: "column", paddingTop: 16 }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-shopping-bag w-16 h-16 text-gray-300 mb-4" data-fg-cxtk1="5.12:5.5215:/components/Cart.tsx:29:9:808:56:e:ShoppingBag::::::sRE"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
        <Typography sx={{ marginBottom: 1 }} color="#6a7282">Twój koszyk jest pusty</Typography>
        <Typography variant="body2" color="#99a1af">Dodaj posiłki z listy aby rozpocząć</Typography>
      </Box>
    )
  }

  const adultDinner = dinner.filter((meal) => !meal.name.toLowerCase().includes('dzieci'));
  const childDinner = dinner.filter((meal) => meal.name.toLowerCase().includes('dzieci'));

  return (
    <Box sx={{ p: 3 }}>
      <div>
        {breakfast.length ? <p>Śniadania</p> : null}
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          {breakfast.map((meal) => (
            <li key={meal.name}>
              <MealItem meal={meal} setCart={handleSetCart} />
            </li>
          ))}
        </ul>
      </div>
      <div>
        {lunch.length ? <p>Obiady</p> : null}
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          {lunch.map((meal) => (
            <li key={meal.name}>
              <MealItem meal={meal} setCart={handleSetCart} />
            </li>
          ))}
        </ul>
      </div>
      <div>
        {adultDinner.length ? <p>Kolacje</p> : null}
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          {adultDinner.map((meal) => (
            <li key={meal.name}>
              <MealItem meal={meal} setCart={handleSetCart} />
            </li>
          ))}
        </ul>
        {childDinner.length ? <p style={{ margin: "10px 0" }}>Kolacje Dzieci</p> : null}
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          {childDinner.map((meal) => (
            <li key={meal.name}>
              <MealItem meal={meal} setCart={handleSetCart} />
            </li>
          ))}
        </ul>
      </div>
      <div>
        {snack.length ? <p>Przekąski</p> : null}
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          {snack.map((meal) => (
            <li key={meal.name}>
              <MealItem meal={meal} setCart={handleSetCart} />
            </li>
          ))}
        </ul>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Button disabled={!cart.length || snackbarStatus === "working"} onClick={handleOrder} variant="contained">Zamów</Button>
        <Button disabled={!cart.length || snackbarStatus === "working"} onClick={addToList} variant="outlined">Dodaj do listy</Button>
        <Button disabled={!cart.length} onClick={clearCart} variant="text">Wyczyść koszyk</Button>
      </div>
      <Snackbar messages={{ working: "Zamawiam", done: "Produkty dodane do koszyka" }} />
    </Box>
  )
}
